import { defaultBlockSpecs, defaultProps } from "@blocknote/core"
import { parseBlockNoteContent } from "./editor-content"
import { normalizeIssueDescription } from "./utils"

// Compare the document users edit, not storage IDs, cached Markdown or object
// insertion order. Use the installed editor's defaults rather than a second list.
function canonicalContent(value: unknown): unknown {
	if (Array.isArray(value)) {
		const result: unknown[] = []
		for (const item of value) {
			const next = canonicalContent(item)
			const previous = result.at(-1)
			if (
				next &&
				typeof next === "object" &&
				"type" in next &&
				next.type === "text" &&
				"text" in next &&
				"styles" in next
			) {
				if (next.text === "") continue
				if (
					previous &&
					typeof previous === "object" &&
					"type" in previous &&
					previous.type === "text" &&
					"text" in previous &&
					"styles" in previous &&
					JSON.stringify(previous.styles) === JSON.stringify(next.styles)
				) {
					previous.text = String(previous.text) + String(next.text)
					continue
				}
			}
			result.push(next)
		}
		return result
	}
	if (!value || typeof value !== "object") return value
	const record = { ...value } as Record<string, unknown>
	const type = record.type
	if (typeof type === "string" && Object.hasOwn(defaultBlockSpecs, type)) {
		const spec =
			defaultBlockSpecs[type as keyof typeof defaultBlockSpecs].config
		delete record.id
		record.props = {
			...Object.fromEntries(
				Object.entries(spec.propSchema).map(([key, prop]) => [
					key,
					prop.default
				])
			),
			...(record.props as object)
		}
		record.children ??= []
		if (spec.content !== "none") record.content ??= []
		else delete record.content
	}
	if (type === "tableCell") {
		record.props = {
			...Object.fromEntries(
				Object.entries(defaultProps).map(([key, prop]) => [key, prop.default])
			),
			colspan: 1,
			rowspan: 1,
			...(record.props as object)
		}
	}
	if (type === "tableContent") {
		record.headerRows ??= 0
		record.headerCols ??= 0
	}
	if (Array.isArray(record.cells))
		record.cells = record.cells.map((cell) =>
			Array.isArray(cell)
				? { type: "tableCell", props: {}, content: cell }
				: cell
		)
	if (record.styles && typeof record.styles === "object") {
		record.styles = Object.fromEntries(
			Object.entries(record.styles).filter(
				([key, style]) =>
					style !== false &&
					!(
						["textColor", "backgroundColor"].includes(key) &&
						style === "default"
					)
			)
		)
	}
	return Object.fromEntries(
		Object.entries(record)
			.filter(([, item]) => item !== undefined)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([key, item]) => [key, canonicalContent(item)])
	)
}

export function getEditorContentFingerprint(value: string | null | undefined) {
	const normalized = normalizeIssueDescription(value)
	if (!normalized) return null
	const document = parseBlockNoteContent(normalized)!
	return JSON.stringify(canonicalContent(document.blocks))
}
